/**
 * Standard Error Types
 * Defines error structures used throughout the application
 */

/** Standard error interface for consistent error handling */
export interface StandardError extends Error {
  message: string;
  code?: string | number;
  statusCode?: number;
  details?: Record<string, unknown>;
  originalError?: Error;
}

/** Type guard to check if value is a StandardError */
export function isStandardError(value: unknown): value is StandardError {
  return (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof (value as Record<string, unknown>).message === "string"
  );
}

/** Type guard to check if value is an Error */
export function isErrorLike(value: unknown): value is Error {
  return (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof (value as Record<string, unknown>).message === "string"
  );
}

/**
 * Safely extract error message from unknown error value
 * @param error - Unknown error value
 * @returns Error message string
 */
export function getErrorMessage(error: unknown): string {
  if (isStandardError(error)) {
    return error.message;
  }
  if (isErrorLike(error)) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "An unknown error occurred";
}

/**
 * Safely extract error code from unknown error value
 * @param error - Unknown error value
 * @returns Error code string or number, or undefined
 */
export function getErrorCode(error: unknown): string | number | undefined {
  if (isStandardError(error)) {
    return error.code;
  }
  return undefined;
}
